import React from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import annotateViewStateToProps from "../selectors";
import * as actions from "../actions";
import AnnotateLayout from "./AnnotateLayout";
import { parseCoordinates } from "../../../../util/leaflet-utils";

const formShape = PropTypes.shape({
  dropStartTime: PropTypes.string,
  dropBottomTime: PropTypes.string,
  startLocation: PropTypes.string,
  bottomLocation: PropTypes.string,
  endLocation: PropTypes.string,
  survey: PropTypes.string,
  siteId: PropTypes.string,
  distanceFromSampleSite: PropTypes.number,
  targetDepthM: PropTypes.number,
  drop: PropTypes.number,
  miscast: PropTypes.bool,
  fieldStaff: PropTypes.string,
  qualityLog: PropTypes.string
});

const castShape = PropTypes.shape({
  ctdCastPk: PropTypes.number,
  startDt: PropTypes.string,
  endDt: PropTypes.string,
  bottomDt: PropTypes.string
});

const fileShape = PropTypes.shape({
  filename: PropTypes.string.isRequired,
  ctdFilePk: PropTypes.number.isRequired,
  numCasts: PropTypes.number,
  uploadedDt: PropTypes.string,
  fileDt: PropTypes.string,
  disabled: PropTypes.bool
});

class AnnotateView extends React.Component {
  static propTypes = {
    possibleForms: PropTypes.objectOf(PropTypes.arrayOf(formShape)),
    filesBeingAnnotated: PropTypes.arrayOf(PropTypes.number),
    filesList: PropTypes.arrayOf(fileShape).isRequired,
    annotationModalCast: castShape,
    chartModalCast: castShape,
    annotationModalForms: PropTypes.arrayOf(formShape),
    annotationModalSelected: PropTypes.number,
    workarea: PropTypes.string,
    actions: PropTypes.shape({
      filesToAnnotateInvalidate: PropTypes.func.isRequired,
      filesToAnnotateFetchIfNeeded: PropTypes.func.isRequired,
      castsToAnnotateFetchIfNeeded: PropTypes.func.isRequired,
      setMatchedForm: PropTypes.func.isRequired,
      updateMatchedForm: PropTypes.func.isRequired,
      updateCasts: PropTypes.func.isRequired,
      annotateFiles: PropTypes.func.isRequired,
      openAnnotationModal: PropTypes.func.isRequired,
      closeAnnotationModal: PropTypes.func.isRequired,
      setAnnotationModalCast: PropTypes.func.isRequired,
      setAnnotationModalForms: PropTypes.func.isRequired,
      setAnnotationModalSelected: PropTypes.func.isRequired,
      addFileToAnnotate: PropTypes.func.isRequired,
      removeFileToAnnotate: PropTypes.func.isRequired,
      closeChartModal: PropTypes.func.isRequired,
      openChartModal: PropTypes.func.isRequired,
      closeMapModal: PropTypes.func.isRequired,
      openMapModal: PropTypes.func.isRequired,
      closeAnnotationSnackbar: PropTypes.func.isRequired,
      setChartModalCast: PropTypes.func.isRequired,
      updateChartModalCast: PropTypes.func.isRequired,
      setMapModalCast: PropTypes.func.isRequired,
      setMapForm: PropTypes.func.isRequired,
      chartDataFetch: PropTypes.func.isRequired,
      chartDataFetchAddStart: PropTypes.func.isRequired,
      chartDataFetchAddEnd: PropTypes.func.isRequired,
      mapSiteDataFetch: PropTypes.func.isRequired
    }).isRequired
  };

  static defaultProps = {
    filesBeingAnnotated: [],
    possibleForms: {},
    annotationModalForms: [],
    annotationModalCast: {},
    chartModalCast: {},
    workarea: null,
    annotationModalSelected: null
  };

  componentDidMount() {
    document.title = "CTD Annotate";
    this.props.actions.filesToAnnotateFetchIfNeeded();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.workarea !== this.props.workarea)
      this.props.actions.filesToAnnotateFetchIfNeeded();
  }

  handleAnnotationModalClose() {
    this.props.actions.closeAnnotationModal();
  }

  handleAnnotationRowSelect(rowIndex) {
    this.props.actions.setAnnotationModalSelected(rowIndex);
  }

  handleAnnotationUpdateClick() {
    const { ctdCastPk } = this.props.annotationModalCast;
    const matchedForm = this.props.annotationModalForms[
      this.props.annotationModalSelected
    ];
    this.props.actions.setMatchedForm({ ctdCastPk, matchedForm });
    this.props.actions.closeAnnotationModal();
  }

  handleFilesTableRowSelect(rowIndex) {
    // Only select row if file has been s3 verified and cast detection worked
    if (this.props.filesList[rowIndex].disabled) return;
    const { ctdFilePk } = this.props.filesList[rowIndex];
    if (this.props.filesBeingAnnotated.includes(ctdFilePk)) {
      this.props.actions.removeFileToAnnotate({ ctdFilePk });
    } else {
      this.props.actions.addFileToAnnotate({ ctdFilePk });
      this.props.actions.castsToAnnotateFetchIfNeeded({ ctdFilePk });
    }
  }

  handleFilesRefreshClick() {
    this.props.actions.filesToAnnotateInvalidate();
    this.props.actions.filesToAnnotateFetchIfNeeded();
  }

  handleCastChangeFormClick(cast) {
    const forms = this.props.possibleForms[cast.ctdCastPk];
    this.props.actions.setAnnotationModalCast(cast);
    this.props.actions.setAnnotationModalForms(forms);
    this.props.actions.setAnnotationModalSelected(undefined);
    this.props.actions.openAnnotationModal();
  }

  handleCastMiscastCheck(ctdCastPk, checked) {
    const update = { miscast: checked };
    this.props.actions.updateMatchedForm({ ctdCastPk, update });
  }

  handleNoCastCheck(ctdFilePk, ctdCastPk, checked) {
    const update = { noCast: checked };
    this.props.actions.updateCasts({ ctdFilePk, ctdCastPk, update });
  }

  handleCastCrewChange(ctdCastPk, crew) {
    const update = { fieldStaff: crew };
    this.props.actions.updateMatchedForm({ ctdCastPk, update });
  }

  handleCastCommentsChange(ctdCastPk, comment) {
    const update = { qualityLog: comment };
    this.props.actions.updateMatchedForm({ ctdCastPk, update });
  }

  handleAnnotateFileClick() {
    this.props.actions.annotateFiles();
  }

  handleChartModalClose() {
    this.props.actions.closeChartModal();
  }

  handleCreateChart(cast) {
    this.props.actions.setChartModalCast(cast);
    this.props.actions.chartDataFetch(cast);
    this.props.actions.openChartModal();
  }

  handleMapModalClose() {
    this.props.actions.closeMapModal();
  }

  async handleCreateMap(cast) {
    this.props.actions.setMapModalCast(cast);

    const startingForm = this.props.possibleForms[cast.ctdCastPk][
      cast.mostLikelyMatchIndex
    ];
    this.props.actions.setMapForm(startingForm);

    const { startLocation, bottomLocation, endLocation } = startingForm;
    const targetLocation = [startLocation, bottomLocation, endLocation].find(
      x => x !== null
    );
    const { lat, long } = parseCoordinates(targetLocation);
    await this.props.actions.mapSiteDataFetch(
      long,
      lat,
      0.5 // radius in degrees - TODO dynamic range based on map zoom?
    ); // TODO a long-running call here gives no feedback to user
    this.props.actions.openMapModal();
  }

  handleUpdateChartStart({ ctdFilePk, chartData }) {
    const update = {
      ctdFilePk,
      measurementDt: chartData[0].measurementDt,
      chartData
    };
    this.props.actions.chartDataFetchAddStart(update);
  }

  handleUpdateChartEnd({ ctdFilePk, chartData }) {
    const update = {
      ctdFilePk,
      measurementDt: chartData[chartData.length - 1].measurementDt,
      chartData
    };
    this.props.actions.chartDataFetchAddEnd(update);
  }

  handleAnnotationSnackbarClose() {
    this.props.actions.closeAnnotationSnackbar();
  }

  handleChartChangeBottomDt({
    modalBottomDatetime,
    modalBottomDepth,
    modalStartDatetime,
    modalStartDepth,
    modalEndDatetime,
    modalEndDepth,
    ctdCastPk,
    ctdFilePk,
    manualOverrideDt
  }) {
    const update = {
      bottomDepth: modalBottomDepth,
      bottomDt: modalBottomDatetime,
      startDepth: modalStartDepth,
      startDt: modalStartDatetime,
      endDepth: modalEndDepth,
      endDt: modalEndDatetime,
      manualOverrideDt
    };
    this.props.actions.updateCasts({ ctdFilePk, ctdCastPk, update });
    this.props.actions.updateChartModalCast(update);
    this.props.actions.closeChartModal();
  }

  render() {
    return (
      <AnnotateLayout
        {...this.props}
        onAnnotationModalClose={() => this.handleAnnotationModalClose()}
        onAnnotationRowSelect={(e, rowIndex) =>
          this.handleAnnotationRowSelect(rowIndex)
        }
        onAnnotationUpdateClick={() => this.handleAnnotationUpdateClick()}
        onFilesTableRowSelect={(e, rowIndex) =>
          this.handleFilesTableRowSelect(rowIndex)
        }
        onFilesRefreshClick={() => this.handleFilesRefreshClick()}
        onCastChangeFormClick={cast => this.handleCastChangeFormClick(cast)}
        onCastMiscastCheck={(ctdCastPk, checked) =>
          this.handleCastMiscastCheck(ctdCastPk, checked)
        }
        onNoCastCheck={(ctdFilePk, ctdCastPk, checked) =>
          this.handleNoCastCheck(ctdFilePk, ctdCastPk, checked)
        }
        onCastCrewChange={(ctdCastPk, crew) =>
          this.handleCastCrewChange(ctdCastPk, crew)
        }
        onCastCommentsChange={(ctdCastPk, comment) =>
          this.handleCastCommentsChange(ctdCastPk, comment)
        }
        onAnnotateFileClick={() => this.handleAnnotateFileClick()}
        onChartModalClose={() => this.handleChartModalClose()}
        onCreateChart={cast => this.handleCreateChart(cast)}
        onChangeChartDepthBounds={payload =>
          this.handleChartChangeBottomDt(payload)
        }
        onMapModalClose={() => this.handleMapModalClose()}
        onCreateMap={cast => this.handleCreateMap(cast)}
        onAnnotationSnackbarClose={() => this.handleAnnotationSnackbarClose()}
        onAddStartDataClick={payload => this.handleUpdateChartStart(payload)}
        onAddEndDataClick={payload => this.handleUpdateChartEnd(payload)}
      />
    );
  }
}

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch)
});

export default withRouter(
  connect(annotateViewStateToProps, mapDispatchToProps)(AnnotateView)
);
